/**
 * WebXRBackend — native AR tracking, where the platform will give it to us.
 *
 * On Android Chrome this hands the whole tracking problem to ARCore, and every
 * failure this project has spent weeks on simply stops existing:
 *
 *   - **Scale is metric by definition.** One unit is one metre. No ground-plane
 *     estimation, no assumed camera height, no ±15%.
 *   - **`local-floor` is gravity-aligned with Y=0 at the floor.** The whole
 *     carry-gravity-across-from-the-IMU dance in AlvaARBackend.worldUp() is
 *     unnecessary — the platform already knows which way is down.
 *   - **Real visual-inertial tracking.** ARCore fuses camera and IMU properly, so
 *     it survives the rotation-dominant motion that repeatedly destroyed AlvaAR's
 *     map (773° of yaw in 32s produced 24 teleports and an unusable map).
 *   - **Hit-testing against real geometry**, so a fragment can sit on the actual
 *     table rather than on a guessed floor plane.
 *
 * ## Why this is not "the Android backend"
 *
 * Selection is by **feature detection**, never by user-agent sniffing. iOS Safari
 * has no `immersive-ar` today, but if it ever ships one this backend picks it up
 * with no code change. Sniffing for Android would actively prevent that.
 *
 * ## dom-overlay is what makes this cheap
 *
 * `immersive-ar` normally expects you to render the scene yourself in WebGL. The
 * `dom-overlay` feature instead composites ordinary DOM on top of the camera
 * feed — so the existing fragment cards, HUD and icons keep working unchanged,
 * and this backend only has to supply poses. Without it, adopting WebXR would
 * mean rewriting the entire presentation layer.
 *
 * ## Session start must happen inside a user gesture
 *
 * Same constraint as iOS motion permission, and the same failure mode if ignored:
 * `requestSession` rejects once the gesture task has ended, and any `await`
 * before it ends the task. `requestXRSession()` must therefore be called from a
 * click handler and the promise handed to `start()` later.
 *
 * ⚠️ DISCONNECTED (2026-10-04). The only caller of `startXRSessionIfAvailable`
 * was the old Enter button in `scenes/Onboarding.tsx`, which the Rite replaced
 * and nothing renders; no live code path requests a session, so `start()`
 * always ends 'unavailable' and the backend chain moves on to AlvaAR. Leaving
 * WebXR disabled is a product decision (unverified on devices, takes over the
 * screen), not an oversight. Re-enabling it means calling
 * `startXRSessionIfAvailable()` as the FIRST statement of the Rite's gesture
 * handler.
 *
 * ⚠️ NOT FIELD-VERIFIED. Written against the WebXR Device API spec; an
 * `immersive-ar` session cannot be exercised headlessly, so nothing here has run
 * on real hardware.
 */

import type { Anchor, CameraPose, Look, MapHealth, SpatialBackend } from './SpatialBackend.js';
import type { FrameProvider } from './FrameProvider.js';
import { telemetry, type TrackingState } from './telemetry.js';
import type { ScaleEstimate } from './GroundPlaneScale.js';

// ---------------------------------------------------------------------------
// Minimal WebXR typings. @types/webxr is not installed and this is all we touch;
// keeping it local avoids a dependency for one backend.
// ---------------------------------------------------------------------------

interface XRRigidTransformLike {
  readonly position: { x: number; y: number; z: number; w: number };
  readonly orientation: { x: number; y: number; z: number; w: number };
}
interface XRViewerPoseLike {
  readonly transform: XRRigidTransformLike;
  readonly emulatedPosition: boolean;
}
interface XRHitTestResultLike {
  getPose(baseSpace: XRSpaceLike): { transform: XRRigidTransformLike } | null | undefined;
}
interface XRSpaceLike {
  readonly __brand?: 'XRSpace';
}
interface XRFrameLike {
  readonly session: XRSessionLike;
  getViewerPose(space: XRSpaceLike): XRViewerPoseLike | null | undefined;
  getHitTestResults(source: XRHitTestSourceLike): XRHitTestResultLike[];
}
interface XRHitTestSourceLike {
  cancel(): void;
}
interface XRSessionLike {
  requestReferenceSpace(type: string): Promise<XRSpaceLike>;
  requestAnimationFrame(cb: (time: number, frame: XRFrameLike) => void): number;
  updateRenderState?(state: { baseLayer?: object }): void;
  requestHitTestSource?(options: { space: XRSpaceLike }): Promise<XRHitTestSourceLike>;
  end(): Promise<void>;
  addEventListener(type: 'end', cb: () => void): void;
  readonly visibilityState?: string;
}
interface XRSystemLike {
  isSessionSupported(mode: string): Promise<boolean>;
  requestSession(mode: string, init?: Record<string, unknown>): Promise<XRSessionLike>;
}

function xr(): XRSystemLike | null {
  const nav = navigator as unknown as { xr?: XRSystemLike };
  return nav.xr ?? null;
}

/**
 * Cached support answer.
 *
 * `isSessionSupported` is async, and awaiting it inside a click handler would
 * consume the user gesture that `requestSession` then needs — the exact
 * trap that silently disabled iOS motion for weeks. So the question is asked
 * once at app start and the answer read synchronously later.
 */
let supported: boolean | null = null;

/** Ask the platform once, early. Safe to call repeatedly. */
export async function primeXRSupport(): Promise<boolean> {
  if (supported !== null) return supported;
  const system = xr();
  if (!system?.isSessionSupported) {
    supported = false;
    return supported;
  }
  try {
    supported = await system.isSessionSupported('immersive-ar');
  } catch {
    supported = false;
  }
  return supported;
}

/** Whether this device can give us a real AR session. Feature detection only. */
export async function webxrAvailable(): Promise<boolean> {
  return primeXRSupport();
}

/**
 * Synchronous, gesture-safe entry point: starts an AR session if we already know
 * one is available. Call as the first statement of a click handler.
 */
export function startXRSessionIfAvailable(root: HTMLElement = document.body): void {
  if (supported !== true) return;
  void requestXRSession(root);
}

// ---------------------------------------------------------------------------
// Session request — MUST be called synchronously from a user gesture.
// ---------------------------------------------------------------------------

let sessionPromise: Promise<XRSessionLike | null> | null = null;
let lastSessionError: string | null = null;

/**
 * Ask for an immersive-ar session. Call this FIRST THING in a click handler,
 * before any `await` — the gesture is consumed by the first suspension point.
 *
 * @param overlayRoot element composited over the camera feed (dom-overlay)
 */
export function requestXRSession(overlayRoot: HTMLElement): Promise<XRSessionLike | null> {
  if (sessionPromise) return sessionPromise;
  const system = xr();
  if (!system) {
    sessionPromise = Promise.resolve(null);
    return sessionPromise;
  }
  sessionPromise = system
    .requestSession('immersive-ar', {
      /*
       * NOTHING is required.
       *
       * `local-floor` was a requiredFeature, which made requestSession reject
       * outright on any device that does not offer it — while start() already
       * contained a perfectly good fallback to the `local` reference space that
       * could therefore never run. A gravity-aligned floor origin is very much
       * worth having, but it is not worth losing the whole session over: `local`
       * still gives metric, drift-free 6DoF tracking, just with Y=0 at the
       * headset's start height instead of the floor.
       */
      optionalFeatures: [
        'local-floor',
        'dom-overlay',
        'hit-test',
        'anchors',
        'light-estimation',
      ],
      domOverlay: { root: overlayRoot },
    })
    .then(
      (s) => {
        lastSessionError = null;
        return s;
      },
      (err: unknown) => {
        lastSessionError = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
        // eslint-disable-next-line no-console -- a refused session is otherwise invisible
        console.error('[WebXR] requestSession failed', err);
        return null;
      },
    );
  return sessionPromise;
}

/** Why the last session request failed, for the HUD. */
export function xrSessionError(): string | null {
  return lastSessionError;
}

/**
 * Mark the document as presenting an XR session.
 *
 * In immersive-ar with dom-overlay the compositor draws the camera BEHIND the
 * overlay root, so the app's own opaque background hides it — which is what
 * showed a black screen on the Samsung tablet. The stylesheet keys the overlay's
 * transparency off this attribute.
 *
 * Guarded because `stop()` must remain callable without a DOM: it runs in the
 * headless fallback tests, and an exception there would abort the very cleanup
 * that lets the next backend claim the camera.
 */
function markXRPresenting(on: boolean): void {
  const root = typeof document !== 'undefined' ? document.documentElement : null;
  if (!root) return;
  if (on) root.setAttribute('data-xr', 'active');
  else root.removeAttribute('data-xr');
}

/** Forget any session (so a later gesture can request a fresh one). */
export function clearXRSession(): void {
  sessionPromise = null;
  lastSessionError = null;
}

const DEG = Math.PI / 180;
/** Fallback placement distance (metres) when nothing is hit-tested. */
const ANCHOR_DEPTH_M = 2.5;
/** Ignore hit-test results further than this — almost certainly a grazing ray. */
const MAX_HIT_DISTANCE_M = 12;

type Vec3 = { x: number; y: number; z: number };
type Quat = { x: number; y: number; z: number; w: number };

function quatRotate(q: Quat, v: [number, number, number]): Vec3 {
  const [vx, vy, vz] = v;
  const tx = 2 * (q.y * vz - q.z * vy);
  const ty = 2 * (q.z * vx - q.x * vz);
  const tz = 2 * (q.x * vy - q.y * vx);
  return {
    x: vx + q.w * tx + (q.y * tz - q.z * ty),
    y: vy + q.w * ty + (q.z * tx - q.x * tz),
    z: vz + q.w * tz + (q.x * ty - q.y * tx),
  };
}

function hashSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

export class WebXRBackend implements SpatialBackend {
  readonly frame = 'world' as const;
  readonly persistent = true;

  private session: XRSessionLike | null = null;
  private refSpace: XRSpaceLike | null = null;
  private viewerSpace: XRSpaceLike | null = null;
  private hitSource: XRHitTestSourceLike | null = null;
  /** Transparent layer that exists only so the compositor has something to present. */
  /**
   * The context backing the compositor's layer. Holding it also keeps its canvas
   * alive (`gl.canvas`), which must outlive the session.
   */
  private gl: WebGLRenderingContext | null = null;

  private position: Vec3 = { x: 0, y: 0, z: 0 };
  private orientation: Quat = { x: 0, y: 0, z: 0, w: 1 };
  /** Latest hit-test point in world space, or null when nothing is in front. */
  private hit: Vec3 | null = null;
  private anchors = new Map<string, Vec3>();
  private state: TrackingState = 'idle';
  private everTracked = false;
  private frameCount = 0;
  private lastFrameAt = 0;

  /**
   * True ONLY while a session is genuinely presenting.
   *
   * This was a hardcoded `= true` on the class, which meant the app removed the
   * passthrough `<video>` and skipped `getUserMedia` purely because this class had
   * been constructed — regardless of whether WebXR was showing anything. Combined
   * with the missing base layer below, that produced a black screen on multiple
   * Android devices: no camera element, and an XR compositor with nothing to
   * draw.
   *
   * A getter means the answer tracks reality. `start()` has always resolved
   * before the field mounts, so by the time this is read the session either
   * exists or does not, and the worst case degrades to the ordinary camera view
   * rather than to black.
   */
  get providesCameraPassthrough(): boolean {
    return this.session !== null && this.state !== 'unavailable';
  }

  // Frames come from the XR compositor, so no FrameProvider is needed. Declared
  // to satisfy the interface; deliberately a no-op.
  setFrameSource(_source: FrameProvider | null): void {
    /* not used — the XR session owns the camera */
  }

  /**
   * Create the transparent WebGL layer the compositor needs, and hand it over.
   *
   * The context must be created `xrCompatible`; otherwise XRWebGLLayer
   * construction throws on devices where the XR compositor runs on a different
   * GPU adapter than the page.
   */
  private attachRenderLayer(session: XRSessionLike): boolean {
    try {
      const canvas = document.createElement('canvas');
      const gl = (canvas.getContext('webgl2', { xrCompatible: true, alpha: true }) ??
        canvas.getContext('webgl', { xrCompatible: true, alpha: true })) as
        | (WebGLRenderingContext & { makeXRCompatible?: () => Promise<void> })
        | null;
      if (!gl) return false;

      const Layer = (
        globalThis as unknown as {
          XRWebGLLayer?: new (s: XRSessionLike, gl: unknown) => object;
        }
      ).XRWebGLLayer;
      if (!Layer) return false;


      this.gl = gl;
      session.updateRenderState?.({ baseLayer: new Layer(session, gl) });
      return true;
    } catch {
      return false;
    }
  }

  private disposeGl(): void {
    try {
      (this.gl as unknown as { getExtension?(n: string): { loseContext(): void } | null })
        ?.getExtension?.('WEBGL_lose_context')
        ?.loseContext();
    } catch {
      /* best effort */
    }
    this.gl = null;

  }

  async start(): Promise<void> {
    telemetry.setBackend('webxr');
    // The session must already have been requested from a click handler.
    const session = await (sessionPromise ?? Promise.resolve(null));
    if (!session) {
      this.state = 'unavailable';
      return;
    }
    this.session = session;
    // Make body/root transparent so the ARCore camera feed shows through the
    // dom-overlay. Without this the app's dark background covers the camera.
    markXRPresenting(true);
    session.addEventListener('end', () => {
      this.state = 'idle';
      this.session = null;
      this.disposeGl();
      markXRPresenting(false);
      clearXRSession();
    });

    /*
     * Give the session something to render.
     *
     * `immersive-ar` is not "camera feed plus DOM" — it is a compositor that
     * expects an XRWebGLLayer, and draws the camera BEHIND whatever that layer
     * contains. With no base layer there is nothing to composite: several Android
     * devices simply showed black, and per spec the session's animation frames
     * are not obliged to run at all.
     *
     * We never draw into it — the artwork is DOM, delivered through dom-overlay —
     * so this is a deliberately empty transparent layer that exists solely to
     * make the session presentable. Cheap, and required.
     */
    if (!this.attachRenderLayer(session)) {
      // Without a layer the session would present nothing. Better to report
      // unavailable and let the runtime fall back to AlvaAR than to show black.
      this.state = 'unavailable';
      lastSessionError = 'could not create an XR WebGL layer';
      return;
    }

    try {
      this.refSpace = await session.requestReferenceSpace('local-floor');
    } catch {
      // Some devices only offer 'local'. Y=0 is then the headset start height
      // rather than the floor, which costs us floor-relative placement but not
      // tracking.
      try {
        this.refSpace = await session.requestReferenceSpace('local');
      } catch {
        this.state = 'unavailable';
        return;
      }
    }

    // Hit-test source anchored to the viewer: "what is straight ahead?"
    try {
      if (session.requestHitTestSource) {
        this.viewerSpace = await session.requestReferenceSpace('viewer');
        this.hitSource = (await session.requestHitTestSource({ space: this.viewerSpace })) ?? null;
      }
    } catch {
      this.hitSource = null; // hit-test is optional; fixed-depth placement still works
    }

    this.state = 'initializing';
    const onFrame = (_time: number, frame: XRFrameLike) => {
      this.onXRFrame(frame);
      this.session?.requestAnimationFrame(onFrame);
    };
    session.requestAnimationFrame(onFrame);
  }

  private onXRFrame(frame: XRFrameLike): void {
    if (!this.refSpace) return;
    const tStart = performance.now();

    const pose = frame.getViewerPose(this.refSpace);
    if (pose) {
      const p = pose.transform.position;
      const o = pose.transform.orientation;
      this.position = { x: p.x, y: p.y, z: p.z };
      this.orientation = { x: o.x, y: o.y, z: o.z, w: o.w };
      // emulatedPosition means the runtime is guessing translation (3DoF
      // fallback) — real 6DoF has it false. Treat guessing as not tracking, since
      // world-locked anchors would be wrong.
      this.state = pose.emulatedPosition ? 'lost' : 'tracking';
      if (this.state === 'tracking') this.everTracked = true;
    } else {
      this.state = this.everTracked ? 'lost' : 'initializing';
    }

    // Where the centre of the view meets real geometry.
    this.hit = null;
    if (this.hitSource && this.refSpace) {
      try {
        const results = frame.getHitTestResults(this.hitSource);
        const first = results[0]?.getPose(this.refSpace);
        if (first) {
          const hp = first.transform.position;
          const d = Math.hypot(hp.x - this.position.x, hp.y - this.position.y, hp.z - this.position.z);
          if (d <= MAX_HIT_DISTANCE_M) this.hit = { x: hp.x, y: hp.y, z: hp.z };
        }
      } catch {
        /* a bad frame must not stop tracking */
      }
    }

    this.frameCount++;
    const now = performance.now();
    const trackMs = now - tStart;
    const look = this.look();
    telemetry.record({
      frameId: this.frameCount,
      state: this.state,
      // WebXR exposes no feature count; report 0 rather than inventing one.
      featureCount: 0,
      pos: this.state === 'tracking' ? [this.position.x, this.position.y, this.position.z] : null,
      yaw: look.yaw,
      pitch: look.pitch,
      // The compositor owns frame acquisition, so there is no grab cost to us.
      grabMs: 0,
      trackMs,
      metersPerUnit: 1,
    });
    this.lastFrameAt = now;
  }

  stop(): void {
    try {
      this.hitSource?.cancel();
    } catch {
      /* ignore */
    }
    this.hitSource = null;
    void this.session?.end().catch(() => undefined);
    this.session = null;
    this.refSpace = null;
    this.disposeGl();
    markXRPresenting(false);
    this.state = 'idle';
    // Drop the cached session promise. Without this a single early failure is
    // remembered for the lifetime of the page, so falling back to AlvaAR would
    // also permanently prevent WebXR being retried.
    clearXRSession();
  }

  look(): Look {
    const f = quatRotate(this.orientation, [0, 0, -1]);
    const u = quatRotate(this.orientation, [0, 1, 0]);
    return {
      yaw: (Math.atan2(f.x, f.z) / DEG + 360) % 360,
      pitch: Math.asin(Math.max(-1, Math.min(1, f.y))) / DEG,
      roll: Math.atan2(u.x, u.y) / DEG,
    };
  }

  anchor(id: string): Anchor {
    if (!this.anchors.has(id)) {
      if (this.state !== 'tracking') {
        // Not tracking — do not write a world coordinate we know to be wrong.
        const f = quatRotate(this.orientation, [0, 0, -1]);
        return {
          id,
          world: {
            x: this.position.x + f.x * ANCHOR_DEPTH_M,
            y: this.position.y + f.y * ANCHOR_DEPTH_M,
            z: this.position.z + f.z * ANCHOR_DEPTH_M,
          },
          confidence: 0,
        };
      }
      const jitter = ((hashSeed(id) % 100) / 100 - 0.5) * 1.6;
      if (this.hit) {
        // Rest on whatever the participant is actually looking at. This is the
        // real surface, from the platform's own geometry — not a guessed plane.
        this.anchors.set(id, {
          x: this.hit.x + jitter * 0.3,
          y: this.hit.y + 0.35, // lift so text is not half-buried in the surface
          z: this.hit.z + jitter * 0.3,
        });
      } else {
        const look = this.look();
        const az = look.yaw * DEG;
        const el = look.pitch * DEG;
        const ce = Math.cos(el);
        this.anchors.set(id, {
          x: this.position.x + ce * Math.sin(az) * ANCHOR_DEPTH_M + Math.cos(az) * jitter,
          y: this.position.y + Math.sin(el) * ANCHOR_DEPTH_M + jitter * 0.4,
          z: this.position.z + ce * Math.cos(az) * ANCHOR_DEPTH_M - Math.sin(az) * jitter,
        });
      }
    }
    return this.resolve(id)!;
  }

  resolve(id: string): Anchor | null {
    const a = this.anchors.get(id);
    if (!a) return null;
    return { id, world: { x: a.x, y: a.y, z: a.z }, confidence: this.state === 'tracking' ? 1 : 0.3 };
  }

  /**
   * The pose the renderer projects with — metric and gravity-aligned, straight
   * from the platform. This is the one backend where the numbers are already in
   * metres, so no scale estimation is involved anywhere in placement.
   */
  cameraPose(): CameraPose {
    return { position: { ...this.position }, orientation: { ...this.orientation } };
  }

  confidence(): number {
    return this.state === 'tracking' ? 1 : 0.3;
  }

  trackingState(): TrackingState {
    return this.state;
  }

  resetTracking(): void {
    this.anchors.clear();
    this.everTracked = false;
  }

  /** WebXR is metric by construction — one unit is one metre, always. */
  metricScale(): ScaleEstimate {
    return {
      state: this.state === 'tracking' ? 'locked' : 'unknown',
      metersPerUnit: this.state === 'tracking' ? 1 : null,
      heightUnits: this.state === 'tracking' ? this.position.y : null,
      samples: this.frameCount,
      spread: 0,
    };
  }

  mapHealth(): MapHealth {
    if (this.state === 'unavailable') {
      return { phase: 'lost', progress: 0, hint: 'AR session unavailable' };
    }
    if (this.state === 'tracking') {
      // Platform tracking, metric scale, gravity known. Nothing to wait for.
      return { phase: 'healthy', progress: 1, hint: null };
    }
    if (this.everTracked) {
      return { phase: 'lost', progress: 0, hint: 'Point at something with more detail' };
    }
    return { phase: 'mapping', progress: 0.4, hint: 'Move the phone gently to start' };
  }

  /** Milliseconds since the last XR frame — a liveness check for the HUD. */
  msSinceFrame(): number {
    return this.lastFrameAt ? performance.now() - this.lastFrameAt : Infinity;
  }

  /**
   * Wait for the compositor to actually deliver a frame.
   *
   * This is the guard that makes auto-selecting WebXR safe. A session can open,
   * report no error, and present nothing — which is exactly what produced a black
   * screen on several Android devices. Per spec a session's animation frames need
   * not run at all if it is not in a presentable state, so **an arriving frame is
   * the evidence that there is a picture**; without one we must fall back rather
   * than hand the participant a dead screen.
   *
   * Deliberately waits for a FRAME, not a pose. Frames prove the compositor is
   * presenting the camera; ARCore often needs several more seconds to resolve a
   * first pose, and refusing to wait that long would reject sessions that go on
   * to work perfectly. Tracking that starts late is reported by `mapHealth()` and
   * shows the participant the mapping guidance — a black screen shows nothing.
   */
  async confirmStarted(timeoutMs = 3000): Promise<boolean> {
    if (this.state === 'unavailable' || !this.session) return false;
    const deadline = performance.now() + timeoutMs;
    while (performance.now() < deadline) {
      if (this.frameCount > 0) return true;
      // The session may also end underneath us (user dismissed, ARCore failure).
      if (!this.session) return false;
      await new Promise((r) => setTimeout(r, 50));
    }
    lastSessionError = `session opened but presented no frames in ${timeoutMs}ms`;
    return false;
  }
}
